import { useState } from 'react';
import { styled } from 'styled-components';

import { Spacer } from '../../spacer/Spacer';

import { VerificationCodeInput } from './VerificationCodeInput';

const SpacerSC = styled(Spacer)`
  display: flex;
  flex-direction: row;

  ${(props) =>
    props.theme.isRtl
      ? `span:first-child {
        &:after {
          display: none;
        }
      }`
      : `span:Last-child {
        &:after {
          display: none;
        }
      }`}
`;

const WrapperSC = styled.div<{ $isValid?: boolean }>`
  background-color: ${(props) => props.theme.colors.slateGray[10]};
  border-radius: 5px;
  max-height: 4.125rem;

  input {
    color: ${(props) =>
      props.$isValid === false ? props.theme.colors.alertRed[100] : props.theme.colors.oxfordBlue[100]};
  }
`;

const LabelWrapperSC = styled.span<{ $isValid?: boolean }>`
  color: ${(props) =>
    props.$isValid === false ? props.theme.colors.alertRed[100] : props.theme.colors.oxfordBlue[100]};
`;

type Props = {
  codeLength?: number;
  id?: string;
  isValid?: boolean;
  label?: React.ReactElement;
  onChange: (code: string[]) => void;
};

export const VerificationCode = ({ codeLength = 5, id = 'pinCodeInput', isValid, label, onChange }: Props) => {
  const emptyCode = Array(codeLength).fill('');
  const [code, setCode] = useState<string[]>(emptyCode);

  function handleCodeChange(event: React.FormEvent<HTMLInputElement>, value: string, index: number) {
    const newCode = [...code];
    const target = event.currentTarget;
    const nextInput = target.parentNode?.nextSibling?.childNodes[0] as HTMLInputElement | null;

    newCode.splice(index, 1, value);
    setCode(newCode);

    onChange(newCode);

    if (value.length && nextInput) {
      nextInput?.focus();
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    const target = event.currentTarget;
    const { key } = event;

    const nextInput = target.parentNode?.nextSibling?.childNodes[0] as HTMLInputElement | null;
    const previousInput = target.parentNode?.previousSibling?.childNodes[0] as HTMLInputElement | null;

    if (key === 'ArrowRight') {
      nextInput?.focus();
    }

    if (key === 'ArrowLeft') {
      previousInput?.focus();
    }

    if (key === 'Backspace' && target.value === '') {
      previousInput?.focus();
    }
  }

  return (
    <>
      {label && <LabelWrapperSC $isValid={isValid}>{label}</LabelWrapperSC>}
      <WrapperSC $isValid={isValid} aria-label="PIN code input" id={id} role="group">
        <SpacerSC $bottom={0.625} $top={0.625}>
          {code.map((num, index) => (
            <VerificationCodeInput
              key={index}
              codeNumber={num}
              handleCodeChange={handleCodeChange}
              handleKeyDown={handleKeyDown}
              index={index}
            />
          ))}
        </SpacerSC>
      </WrapperSC>
    </>
  );
};
